import React, { useState, useEffect, useRef } from "react";
import { X, Calendar, FileText, Upload, Save, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { 
  InformacionGeneral, 
  modificarInformacionGeneral, 
  insertarArchivoInformacionGeneral,
  obtenerTodosInformacionGeneral
} from "../../../../api/informacionGeneralService";

interface DetailInformacionGeneralModalProps {
  isOpen: boolean;
  onClose: (shouldReload: boolean) => void;
  ige_codigo: number;
}

export default function DetailInformacionGeneralModal({
  isOpen,
  onClose,
  ige_codigo
}: DetailInformacionGeneralModalProps) {
  const { session } = useAuth();
  const [item, setItem] = useState<InformacionGeneral | null>(null);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [activa, setActiva] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDetails = async () => {
    try {
      setIsLoading(true);
      const unidadCodigo = session?.unidad?.id || "";
      const response = await obtenerTodosInformacionGeneral({
        unidad_residencial_codigo: unidadCodigo,
        codigo: ige_codigo.toString(),
        activa: ""
      });

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        const data = response.object_[0];
        setItem(data);
        setTitulo(data.ige_titulo || "");
        setDescripcion(data.ige_descripcion || "");
        setActiva(data.ige_activa);
      } else {
        Swal.fire("Error", "No se encontró el registro seleccionado.", "error");
        onClose(false);
      }
    } catch (error) {
      console.error("Error fetching detail:", error);
      Swal.fire("Error", "Ocurrió un error al cargar los detalles.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && ige_codigo) {
      fetchDetails();
    }
  }, [isOpen, ige_codigo]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const response = await modificarInformacionGeneral({
        codigo: ige_codigo.toString(),
        titulo: titulo,
        descripcion: descripcion,
        activa: activa.toString()
      });

      if (response.code === "1") {
        await Swal.fire({
          icon: "success",
          title: "¡Guardado!",
          text: "La información ha sido actualizada correctamente.",
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: "#3d5a40",
        });
        onClose(true);
      } else {
        Swal.fire("Error", response.message || "No se pudo guardar la información.", "error");
      }
    } catch (error) {
      console.error("Error saving:", error);
      Swal.fire("Error", "Error de conexión al intentar guardar.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const response = await insertarArchivoInformacionGeneral({
        iga_informacion_general: ige_codigo.toString(),
        iga_archivo: file
      });

      if (response.code === "1") {
        await Swal.fire({
          icon: "success",
          title: "Archivo Cargado",
          text: "El archivo ha sido vinculado exitosamente.",
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: "#3d5a40",
        });
        // Recargar la modal para mostrar el nuevo archivo
        fetchDetails();
      } else {
        Swal.fire("Error", response.message || "Error al subir el archivo.", "error");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      Swal.fire("Error", "Error de conexión al subir el archivo.", "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col border border-gray-100 animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-military-600 p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FileText size={20} />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">Detalle de Información</h3>
              <p className="text-xs text-white/70">Código: #{ige_codigo}</p>
            </div>
          </div>
          <button 
            onClick={() => onClose(false)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[80vh]">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-military-500" />
              <p className="text-gray-500 font-medium">Cargando detalles...</p>
            </div>
          ) : item ? (
            <>
              {/* Info & Title */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <Calendar className="text-military-500" size={20} />
                  <div>
                    <p className="text-[10px] uppercase font-bold text-gray-400">Fecha de Creación</p>
                    <p className="text-sm font-semibold text-gray-700">{item.ige_fecha}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-500 uppercase">Título</label>
                  <input
                    type="text"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-military-500 focus:border-military-500 transition-all text-sm bg-gray-50/50"
                  />
                </div>
              </div>

              {/* Editable Description */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                  Descripción
                </label>
                <textarea
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  className="w-full min-h-[120px] p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-military-500 focus:border-military-500 transition-all text-sm resize-none bg-gray-50/50"
                  placeholder="Escribe la descripción aquí..."
                />
              </div>

              {/* Toggle SI/NO (Radio Group) */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-gray-500 uppercase">
                  ¿Mostrar a residentes?
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiva(1)}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-bold text-sm
                      ${activa === 1 
                        ? 'border-military-500 bg-military-50 text-military-700 shadow-sm' 
                        : 'border-gray-100 bg-white text-gray-400 hover:border-gray-200'}`}
                  >
                    <CheckCircle2 size={18} />
                    SÍ
                  </button>
                  <button
                    onClick={() => setActiva(0)}
                    className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all font-bold text-sm
                      ${activa === 0 
                        ? 'border-red-500 bg-red-50 text-red-700 shadow-sm' 
                        : 'border-gray-100 bg-white text-gray-400 hover:border-gray-200'}`}
                  >
                    <AlertCircle size={18} />
                    NO
                  </button>
                </div>
              </div>

              {/* File Section */}
              <div className="space-y-3 pt-2">
                <label className="text-xs font-bold text-gray-500 uppercase">Documento adjunto</label>
                <div className="flex items-center gap-4">
                  {/* Icon Button */}
                  <div className="relative group">
                    <button
                      disabled={item.ige_archivo_esta_cargado !== "1"}
                      onClick={() => window.open(item.ige_ruta_archivo, "_blank")}
                      className={`w-16 h-16 rounded-xl border-2 flex items-center justify-center p-2 transition-all
                        ${item.ige_archivo_esta_cargado === "1"
                          ? 'border-military-200 bg-military-50 hover:border-military-400 hover:shadow-md cursor-pointer'
                          : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-50'}`}
                    >
                      <img 
                        src={item.ige_tipo_archivo} 
                        alt="Icono archivo" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </button>
                    {item.ige_archivo_esta_cargado === "1" && (
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white p-1 rounded-full shadow-sm">
                        <CheckCircle2 size={12} />
                      </span>
                    )}
                  </div>

                  {/* Upload Info & Button */}
                  <div className="flex-1 space-y-2">
                    <p className="text-[10px] text-gray-500 leading-tight">
                      {item.ige_archivo_esta_cargado === "1" 
                        ? "Ya existe un archivo vinculado. Puedes reemplazarlo cargando uno nuevo."
                        : "No hay archivos vinculados a este registro."}
                    </p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-xs py-2 rounded-lg"
                    >
                      {isUploading ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <Upload size={14} />
                      )}
                      {item.ige_archivo_esta_cargado === "1" ? "Reemplazar Archivo" : "Cargar Archivo"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Action Button */}
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="w-full bg-military-600 hover:bg-military-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-3 shadow-lg shadow-military-100 transition-all active:scale-95"
          >
            {isSaving ? (
              <Loader2 className="animate-spin" size={20} />
            ) : (
              <Save size={20} />
            )}
            GUARDAR CAMBIOS
          </Button>
        </div>
      </div>
    </div>
  );
}
